import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { DateField } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { MdPhoneEnabled } from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
// Components
import { Formtitle, IperiaLayout } from '../../components'
// Hooks
import { useIperiaStatus } from '../../hooks/useIperiaStatus'
// Utils
import { emailValidator, phoneNumberValidator } from '../../utils/validators'
import { getApiErrorMessage } from '../../utils/apiErrors'
import { IperiaFormDefaultState, csp_choices, study_level_choices } from '../../utils/constants'
import { IperiaFormType } from '../../utils/sharedTypes'

const TITLE_CHOICES = [
  { value: 'M.', label: 'M.' },
  { value: 'Mme', label: 'Mme' },
]

type EditableSummaryKeys = 'first_name' | 'last_name' | 'email' | 'mobile_phone' | 'birth_date' | 'birth_name' | 'csp' | 'study_level' | 'title'

const Verification = () => {
  const navigate = useNavigate()
  const { global_id: globalId } = useParams<{ global_id: string }>()
  const { status, loading, error, updateSummary } = useIperiaStatus()
  const [form, setForm] = useState<IperiaFormType>(IperiaFormDefaultState)
  const [errors, setErrors] = useState<Partial<Record<EditableSummaryKeys, string>>>({})
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!status?.learner_summary) return
    setForm({
      ...IperiaFormDefaultState,
      data: {
        ...IperiaFormDefaultState.data,
        ...status.learner_summary
      }
    })
  }, [status?.learner_summary])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !status) {
    return (
      <Typography color="error" sx={{ p: 2 }}>
        Impossible de charger votre dossier.
      </Typography>
    )
  }

  const alreadyConfirmed = status.learner_summary_confirmed
  const formDisabled = alreadyConfirmed

  const handleChange = (key: EditableSummaryKeys, value: string) => {
    setSubmitError(null)
    setErrors((prev) => {
      const next = { ...prev }
      delete next[key]

      if (key === 'title' && !value) {
        next.title = 'Veuillez renseigner votre civilité'
      } else if (key === 'first_name' && !value.trim()) {
        next.first_name = 'Veuillez renseigner votre prénom'
      } else if (key === 'last_name' && !value.trim()) {
        next.last_name = 'Veuillez renseigner votre nom'
      } else if (key === 'email' && !emailValidator(value)) {
        next.email = 'Veuillez renseigner un e-mail valide'
      } else if (key === 'mobile_phone' && !phoneNumberValidator(value)) {
        next.mobile_phone = 'Veuillez renseigner un numéro de téléphone valide'
      } else if (key === 'birth_date') {
        if (!value || !dayjs(value).isValid()) {
          next.birth_date = 'Veuillez entrer une date de naissance valide'
        } else if (dayjs(value).isAfter(dayjs().subtract(16, 'year'), 'day')) {
          next.birth_date = "L'âge minimum requis est de 16 ans"
        }
      }
      return next
    })

    setForm((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        [key]: value,
      },
    }))
  }

  const validateForm = () => {
    const next: Partial<Record<EditableSummaryKeys, string>> = {}
    if (!form.data.title) next.title = 'Veuillez renseigner votre civilité'
    if (!form.data.first_name.trim()) next.first_name = 'Veuillez renseigner votre prénom'
    if (!form.data.last_name.trim()) next.last_name = 'Veuillez renseigner votre nom'
    if (!emailValidator(form.data.email)) next.email = 'Veuillez renseigner un e-mail valide'
    if (!phoneNumberValidator(form.data.mobile_phone)) {
      next.mobile_phone = 'Veuillez renseigner un numéro de téléphone valide'
    }
    if (!form.data.birth_date || !dayjs(form.data.birth_date).isValid()) {
      next.birth_date = 'Veuillez entrer une date de naissance valide'
    } else if (dayjs(form.data.birth_date).isAfter(dayjs().subtract(16, 'year'), 'day')) {
      next.birth_date = "L'âge minimum requis est de 16 ans"
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleNext = async () => {
    if (!globalId) return

    if (alreadyConfirmed) {
      navigate(`/iperia/${globalId}/verification/adresse`)
      return
    }
    if (!validateForm()) return

    setSubmitting(true)
    setSubmitError(null)
    try {
      await updateSummary({
        first_name: form.data.first_name,
        last_name: form.data.last_name,
        email: form.data.email,
        mobile_phone: form.data.mobile_phone,
        birth_date: form.data.birth_date,
        birth_name: form.data.birth_name,
        csp: form.data.csp,
        study_level: form.data.study_level,
        title: form.data.title,
      })

      navigate(`/iperia/${globalId}/verification/adresse`)
    } catch (err) {
      console.error('Failed to update summary:', err)
      setSubmitError(getApiErrorMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <IperiaLayout
      productName={form.data.product_name}
      handleNext={handleNext}
      submitting={submitting}
      submittingNext={submitting}
      alreadyConfirmed={alreadyConfirmed}
    >
      <Formtitle
        title="Étape 1 : Informations personnelles"
        subtitle={
          alreadyConfirmed
            ? 'Vos informations ont déjà été confirmées. Vous pouvez continuer le parcours.'
            : 'Veuillez vérifier et corriger si besoin les informations ci-dessous avant de continuer.'
        }
      />
      {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}
      <Grid container spacing={2} className='w-full'>
        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            label="Civilité"
            id="title"
            required
            disabled={formDisabled}
            value={form.data.title || ''}
            onChange={(e) => handleChange('title', e.target.value)}
            error={Boolean(errors.title)}
            helperText={errors.title}
          >
            {TITLE_CHOICES.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Nom de naissance"
            inputProps={{ autoComplete: 'family-name' }}
            id="birth_name"
            disabled={formDisabled}
            value={form.data.birth_name}
            onChange={(e) => handleChange('birth_name', e.target.value)}
            helperText="Optionnel, si différent du nom d’usage"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Prénom"
            inputProps={{ autoComplete: 'given-name' }}
            id="first_name"
            required
            disabled={formDisabled}
            value={form.data.first_name}
            onChange={(e) => handleChange('first_name', e.target.value)}
            error={Boolean(errors.first_name)}
            helperText={errors.first_name}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Nom d’usage"
            inputProps={{ autoComplete: 'family-name' }}
            id="last_name"
            required
            disabled={formDisabled}
            value={form.data.last_name}
            onChange={(e) => handleChange('last_name', e.target.value)}
            error={Boolean(errors.last_name)}
            helperText={errors.last_name}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <DateField
            id="birth_date"
            label="Date de naissance"
            required
            disabled={formDisabled}
            format="DD/MM/YYYY"
            value={form.data.birth_date ? dayjs(form.data.birth_date) : null}
            fullWidth
            onChange={(value) => {
              if (!value) {
                handleChange('birth_date', '')
              } else if (dayjs(value).isValid() && `${dayjs(value).year()}`.length === 4) {
                handleChange('birth_date', dayjs(value).format('YYYY-MM-DD'))
              }
            }}
            slotProps={{
              textField: {
                // Clavier numérique : la date se tape chiffre par chiffre (JJ/MM/AAAA).
                inputProps: { inputMode: 'numeric', autoComplete: 'bday' },
                onBlur: () => {
                  if (!dayjs(form.data.birth_date).isValid()) {
                    setErrors((prev) => ({
                      ...prev,
                      birth_date: 'Veuillez entrer une date de naissance valide',
                    }))
                  }
                },
                error: Boolean(errors.birth_date),
                helperText: errors.birth_date,
              },
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} className='w-full' sx={{ mt: 1 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="email"
            label="Adresse e-mail"
            inputProps={{ autoComplete: 'email', autoCapitalize: 'none' }}
            id="email"
            required
            disabled={formDisabled}
            value={form.data.email}
            onChange={(e) => handleChange('email', e.target.value)}
            error={Boolean(errors.email)}
            helperText={errors.email}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            type="tel"
            fullWidth
            id="mobile_phone"
            label="N° de mobile"
            inputProps={{ autoComplete: 'tel', inputMode: 'tel' }}
            required
            disabled={formDisabled}
            InputProps={{
              endAdornment: (
                <InputAdornment position="start">
                  <MdPhoneEnabled size="20" />
                </InputAdornment>
              ),
            }}
            value={form.data.mobile_phone}
            onChange={(e) => handleChange('mobile_phone', e.target.value)}
            error={Boolean(errors.mobile_phone)}
            helperText={errors.mobile_phone}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth disabled={formDisabled}>
            <InputLabel id="study_level-label">Niveau d'études</InputLabel>
            <Select
              labelId="study_level-label"
              id="study_level"
              label="Niveau d'études"
              value={form.data.study_level ?? ''}
              onChange={(e) => handleChange('study_level', e.target.value)}
            >
              {study_level_choices.map((choice) => (
                <MenuItem key={choice.value} value={choice.value}>
                  {choice.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth disabled={formDisabled}>
            <InputLabel id="csp-label">Catégorie socio-professionnelle</InputLabel>
            <Select
              labelId="csp-label"
              id="csp"
              label="Catégorie socio-professionnelle"
              value={form.data.csp ?? ''}
              onChange={(e) => handleChange('csp', e.target.value)}
            >
              {csp_choices.map((choice) => (
                <MenuItem key={choice.value} value={choice.value}>
                  {choice.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </IperiaLayout>
  )
}

export default Verification
